import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Assessment as EvaluateIcon,
  EmojiEvents as AwardIcon,
  Business as VendorIcon,
  RequestQuote as RFQIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Publish as PublishIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const RFQs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [procurementItems, setProcurementItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view, evaluate
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    dateFrom: null,
    dateTo: null
  });
  const [stats, setStats] = useState({
    totalRFQs: 0,
    activeRFQs: 0,
    averageResponseTime: 0,
    totalSavings: 0
  });

  const [formData, setFormData] = useState({
    rfqNumber: '',
    title: '',
    description: '',
    project: '',
    items: [],
    vendors: [],
    submissionDeadline: null,
    deliveryLocation: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    evaluationCriteria: {
      price: 40,
      quality: 30,
      delivery: 20,
      experience: 10
    },
    terms: '',
    notes: ''
  });

  const [newItem, setNewItem] = useState({
    item: '',
    description: '',
    quantity: 1,
    unit: 'pcs',
    specifications: ''
  });

  const [evaluationData, setEvaluationData] = useState({
    quotes: [],
    selectedVendor: '',
    evaluationNotes: ''
  });

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'published', label: 'Published', color: 'info' },
    { value: 'responses_received', label: 'Responses Received', color: 'warning' },
    { value: 'under_evaluation', label: 'Under Evaluation', color: 'primary' },
    { value: 'awarded', label: 'Awarded', color: 'success' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' },
    { value: 'expired', label: 'Expired', color: 'error' }
  ];

  const units = ['pcs', 'kg', 'lbs', 'ft', 'm', 'sqft', 'sqm', 'gal', 'L', 'box', 'roll', 'sheet'];

  useEffect(() => {
    fetchRFQs();
    fetchVendors();
    fetchProcurementItems();
    fetchStats();
  }, [filters]);

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
      
      const response = await api.get(`/rfqs?${params.toString()}`);
      setRfqs(response.data);
    } catch (error) {
      setError('Failed to fetch RFQs');
      console.error('Error fetching RFQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors?status=active');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchProcurementItems = async () => {
    try {
      const response = await api.get('/procurement-items');
      setProcurementItems(response.data);
    } catch (error) {
      console.error('Error fetching procurement items:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/rfqs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching RFQ stats:', error);
    }
  };

  const handleOpenDialog = (mode, rfq = null) => {
    setDialogMode(mode);
    setSelectedRFQ(rfq);
    setActiveTab(0);
    
    if (rfq && mode !== 'create') {
      setFormData({
        rfqNumber: rfq.rfqNumber || '',
        title: rfq.title || '',
        description: rfq.description || '',
        project: rfq.project || '',
        items: rfq.items || [],
        vendors: rfq.vendors?.map(v => v._id || v) || [],
        submissionDeadline: rfq.submissionDeadline ? new Date(rfq.submissionDeadline) : null,
        deliveryLocation: rfq.deliveryLocation || {
          street: '',
          city: '',
          state: '',
          zipCode: ''
        },
        evaluationCriteria: rfq.evaluationCriteria || {
          price: 40,
          quality: 30,
          delivery: 20,
          experience: 10
        },
        terms: rfq.terms || '',
        notes: rfq.notes || ''
      });
      
      if (mode === 'evaluate') {
        setEvaluationData({
          quotes: rfq.quotes || [],
          selectedVendor: '',
          evaluationNotes: ''
        });
      }
    } else {
      setFormData({
        rfqNumber: '',
        title: '',
        description: '',
        project: '',
        items: [],
        vendors: [],
        submissionDeadline: null,
        deliveryLocation: {
          street: '',
          city: '',
          state: '',
          zipCode: ''
        },
        evaluationCriteria: {
          price: 40,
          quality: 30,
          delivery: 20,
          experience: 10
        },
        terms: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRFQ(null);
    setNewItem({
      item: '',
      description: '',
      quantity: 1,
      unit: 'pcs',
      specifications: ''
    });
    setEvaluationData({
      quotes: [],
      selectedVendor: '',
      evaluationNotes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await api.post('/rfqs', formData);
        setSuccess('RFQ created successfully');
      } else if (dialogMode === 'edit') {
        await api.put(`/rfqs/${selectedRFQ._id}`, formData);
        setSuccess('RFQ updated successfully');
      } else if (dialogMode === 'evaluate') {
        await api.post(`/rfqs/${selectedRFQ._id}/award`, {
          vendorId: evaluationData.selectedVendor,
          notes: evaluationData.evaluationNotes
        });
        setSuccess('RFQ awarded successfully');
      }
      
      handleCloseDialog();
      fetchRFQs();
      fetchStats();
    } catch (error) {
      setError('Failed to save RFQ');
      console.error('Error saving RFQ:', error);
    }
  };

  const handleDelete = async (rfqId) => {
    if (window.confirm('Are you sure you want to delete this RFQ?')) {
      try {
        await api.delete(`/rfqs/${rfqId}`);
        setSuccess('RFQ deleted successfully');
        fetchRFQs();
        fetchStats();
      } catch (error) {
        setError('Failed to delete RFQ');
        console.error('Error deleting RFQ:', error);
      }
    }
  };

  const handlePublish = async (rfqId) => {
    try {
      await api.post(`/rfqs/${rfqId}/publish`);
      setSuccess('RFQ published successfully');
      fetchRFQs();
      fetchStats();
    } catch (error) {
      setError('Failed to publish RFQ');
      console.error('Error publishing RFQ:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddItem = () => {
    if (newItem.item && newItem.quantity > 0) {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, { ...newItem }]
      }));
      setNewItem({
        item: '',
        description: '',
        quantity: 1,
        unit: 'pcs',
        specifications: ''
      });
    }
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'default';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.label : status;
  };

  const calculateQuoteScore = (quote) => {
    const criteria = selectedRFQ?.evaluationCriteria || formData.evaluationCriteria;
    const priceScore = quote.priceScore || 0;
    const qualityScore = quote.qualityScore || 0;
    const deliveryScore = quote.deliveryScore || 0;
    const experienceScore = quote.experienceScore || 0;
    
    return (
      (priceScore * criteria.price / 100) +
      (qualityScore * criteria.quality / 100) +
      (deliveryScore * criteria.delivery / 100) +
      (experienceScore * criteria.experience / 100)
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading RFQs...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Request for Quotations (RFQ)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/rfqs/create')}
        >
          Create RFQ
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RFQIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total RFQs
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalRFQs}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active RFQs
                  </Typography>
                  <Typography variant="h5">
                    {stats.activeRFQs}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MoneyIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Savings
                  </Typography>
                  <Typography variant="h5">
                    ${stats.totalSavings?.toLocaleString() || '0'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Response Time
                  </Typography>
                  <Typography variant="h5">
                    {stats.averageResponseTime || '0'} days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search RFQs"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="From Date"
                value={filters.dateFrom}
                onChange={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="To Date"
                value={filters.dateTo}
                onChange={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({ status: '', search: '', dateFrom: null, dateTo: null })}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* RFQs Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>RFQ Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Vendors</TableCell>
                  <TableCell>Submission Deadline</TableCell>
                  <TableCell>Responses</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rfqs.map((rfq) => (
                  <TableRow key={rfq._id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {rfq.rfqNumber}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(rfq.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rfq.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {rfq.project}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(rfq.status)}
                        size="small"
                        color={getStatusColor(rfq.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rfq.vendors?.length || 0} vendors
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rfq.submissionDeadline ? 
                          new Date(rfq.submissionDeadline).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rfq.quotes?.length || 0} responses
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('view', rfq)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {rfq.status === 'draft' && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('edit', rfq)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Publish">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePublish(rfq._id)}
                            >
                              <PublishIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {['responses_received', 'under_evaluation'].includes(rfq.status) && (
                        <Tooltip title="Evaluate & Award">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenDialog('evaluate', rfq)}
                          >
                            <EvaluateIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {rfq.status === 'draft' && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(rfq._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* RFQ Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create RFQ' : 
           dialogMode === 'edit' ? 'Edit RFQ' : 
           dialogMode === 'evaluate' ? 'Evaluate RFQ' : 'RFQ Details'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'evaluate' ? (
            // Evaluation Mode
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Evaluate Quotes for: {selectedRFQ?.title}
              </Typography>
              
              {evaluationData.quotes.length > 0 ? (
                <>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vendor</TableCell>
                          <TableCell>Total Price</TableCell>
                          <TableCell>Delivery Time</TableCell>
                          <TableCell>Score</TableCell>
                          <TableCell>Select</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {evaluationData.quotes.map((quote, index) => (
                          <TableRow key={index}>
                            <TableCell>{quote.vendor?.name}</TableCell>
                            <TableCell>${quote.totalPrice?.toLocaleString()}</TableCell>
                            <TableCell>{quote.deliveryTime} days</TableCell>
                            <TableCell>{calculateQuoteScore(quote).toFixed(1)}</TableCell>
                            <TableCell>
                              <Button
                                variant={evaluationData.selectedVendor === quote.vendor?._id ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setEvaluationData(prev => ({
                                  ...prev,
                                  selectedVendor: quote.vendor?._id
                                }))}
                              >
                                {evaluationData.selectedVendor === quote.vendor?._id ? 'Selected' : 'Select'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TextField
                    fullWidth
                    label="Evaluation Notes"
                    multiline
                    rows={4}
                    value={evaluationData.evaluationNotes}
                    onChange={(e) => setEvaluationData(prev => ({
                      ...prev,
                      evaluationNotes: e.target.value
                    }))}
                  />
                </>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                  No quotes received yet
                </Typography>
              )}
            </Box>
          ) : (
            // Create/Edit/View Mode
            <Box sx={{ mt: 2 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="Basic Info" />
                <Tab label="Items" />
                <Tab label="Vendors" />
                <Tab label="Evaluation" />
              </Tabs>
              
              {/* Basic Info Tab */}
              {activeTab === 0 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="RFQ Number"
                      value={formData.rfqNumber}
                      onChange={(e) => handleInputChange('rfqNumber', e.target.value)}
                      disabled={dialogMode === 'view'}
                      placeholder="Auto-generated if empty"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Submission Deadline"
                      value={formData.submissionDeadline}
                      onChange={(date) => handleInputChange('submissionDeadline', date)}
                      disabled={dialogMode === 'view'}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  
                  {/* Delivery Location */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Delivery Location
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.deliveryLocation.street}
                      onChange={(e) => handleInputChange('deliveryLocation.street', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.deliveryLocation.city}
                      onChange={(e) => handleInputChange('deliveryLocation.city', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.deliveryLocation.state}
                      onChange={(e) => handleInputChange('deliveryLocation.state', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Zip Code"
                      value={formData.deliveryLocation.zipCode}
                      onChange={(e) => handleInputChange('deliveryLocation.zipCode', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Terms & Conditions"
                      multiline
                      rows={3}
                      value={formData.terms}
                      onChange={(e) => handleInputChange('terms', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                </Grid>
              )}
              
              {/* Items Tab */}
              {activeTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  {dialogMode !== 'view' && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={3}>
                        <Autocomplete
                          options={procurementItems}
                          getOptionLabel={(option) => option.name || ''}
                          value={procurementItems.find(item => item._id === newItem.item) || null}
                          onChange={(event, newValue) => {
                            setNewItem(prev => ({
                              ...prev,
                              item: newValue?.name || '',
                              description: newValue?.description || ''
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Select Item" fullWidth />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          label="Quantity"
                          type="number"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControl fullWidth>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            value={newItem.unit}
                            label="Unit"
                            onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                          >
                            {units.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Specifications"
                          value={newItem.specifications}
                          onChange={(e) => setNewItem(prev => ({ ...prev, specifications: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleAddItem}
                          sx={{ height: '56px' }}
                        >
                          <AddIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                  
                  {formData.items.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Specifications</TableCell>
                            {dialogMode !== 'view' && <TableCell>Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.item}</TableCell>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.quantity} {item.unit}</TableCell>
                              <TableCell>{item.specifications}</TableCell>
                              {dialogMode !== 'view' && (
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveItem(index)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                      No items added yet
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Vendors Tab */}
              {activeTab === 2 && (
                <Box sx={{ mt: 2 }}>
                  <Autocomplete
                    multiple
                    options={vendors}
                    getOptionLabel={(option) => option.name}
                    value={vendors.filter(vendor => formData.vendors.includes(vendor._id))}
                    onChange={(event, newValue) => {
                      handleInputChange('vendors', newValue.map(v => v._id));
                    }}
                    disabled={dialogMode === 'view'}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Vendors"
                        placeholder="Choose vendors to send RFQ"
                      />
                    )}
                  />
                </Box>
              )}
              
              {/* Evaluation Criteria Tab */}
              {activeTab === 3 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Evaluation Criteria (Total: 100%)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Price Weight (%)"
                        type="number"
                        value={formData.evaluationCriteria.price}
                        onChange={(e) => handleInputChange('evaluationCriteria.price', parseInt(e.target.value))}
                        disabled={dialogMode === 'view'}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Quality Weight (%)"
                        type="number"
                        value={formData.evaluationCriteria.quality}
                        onChange={(e) => handleInputChange('evaluationCriteria.quality', parseInt(e.target.value))}
                        disabled={dialogMode === 'view'}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Delivery Weight (%)"
                        type="number"
                        value={formData.evaluationCriteria.delivery}
                        onChange={(e) => handleInputChange('evaluationCriteria.delivery', parseInt(e.target.value))}
                        disabled={dialogMode === 'view'}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Experience Weight (%)"
                        type="number"
                        value={formData.evaluationCriteria.experience}
                        onChange={(e) => handleInputChange('evaluationCriteria.experience', parseInt(e.target.value))}
                        disabled={dialogMode === 'view'}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Total: {Object.values(formData.evaluationCriteria).reduce((sum, val) => sum + val, 0)}%
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 
               dialogMode === 'edit' ? 'Update' : 
               dialogMode === 'evaluate' ? 'Award RFQ' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RFQs;